import React, { useState } from 'react';
import { Scatter } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import './Pages.css';

ChartJS.register(
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  ChartDataLabels
);

// Register custom background plugin
const backgroundPlugin = {
  id: 'backgroundPlugin',
  beforeDraw: (chart) => {
    const ctx = chart.ctx;
    const chartArea = chart.chartArea;
    const width = chartArea.right - chartArea.left;
    const height = chartArea.bottom - chartArea.top;
    const midX = chartArea.left + width / 2;
    const midY = chartArea.top + height / 2;

    // Draw quadrants with different colors
    // Bottom Left - Light Blue
    ctx.fillStyle = 'rgba(59, 130, 246, 0.1)';
    ctx.fillRect(chartArea.left, midY, width / 2, height / 2);

    // Top Left - Medium Blue
    ctx.fillStyle = 'rgba(59, 130, 246, 0.2)';
    ctx.fillRect(chartArea.left, chartArea.top, width / 2, height / 2);

    // Bottom Right - Medium Blue
    ctx.fillStyle = 'rgba(59, 130, 246, 0.2)';
    ctx.fillRect(midX, midY, width / 2, height / 2);

    // Top Right - Dark Blue
    ctx.fillStyle = 'rgba(59, 130, 246, 0.3)';
    ctx.fillRect(midX, chartArea.top, width / 2, height / 2);
  }
};

ChartJS.register(backgroundPlugin);

const topicSuggestions = {
  environmental: [
    "Climate Change & GHG Emissions",
    "Energy Management",
    "Water Management",
    "Waste & Hazardous Materials",
    "Biodiversity Impact",
    "Air Quality",
    "Resource Efficiency",
    "Environmental Compliance",
    "Renewable Energy Transition",
    "Circular Economy"
  ],
  social: [
    "Employee Health & Safety",
    "Labor Rights & Practices",
    "Diversity & Inclusion",
    "Community Relations",
    "Data Privacy & Security",
    "Product Safety & Quality",
    "Supply Chain Management",
    "Human Rights",
    "Customer Welfare",
    "Talent Management"
  ],
  governance: [
    "Business Ethics",
    "Board Structure & Independence",
    "Risk Management",
    "Anti-corruption",
    "Regulatory Compliance",
    "Shareholder Rights",
    "Executive Compensation",
    "Tax Transparency",
    "Lobbying & Political Contributions",
    "ESG Oversight"
  ]
};

const MaterialityAnalysis = () => {
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [newTopic, setNewTopic] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('environmental');

  const addTopic = (topicName = newTopic.trim()) => {
    if (topicName) {
      setSelectedTopics([...selectedTopics, {
        id: Date.now(),
        name: topicName,
        category: selectedCategory,
        financialScore: 0,
        impactScore: 0
      }]);
      setNewTopic('');
    }
  };

  const removeTopic = (id) => {
    setSelectedTopics(selectedTopics.filter(topic => topic.id !== id));
  };

  const getChartData = () => {
    const datasets = [
      {
        label: 'Environmental',
        data: selectedTopics
          .filter(topic => topic.category === 'environmental')
          .map(topic => ({
            x: topic.financialScore,
            y: topic.impactScore,
            label: topic.name
          })),
        backgroundColor: 'rgba(34, 197, 94, 0.6)',
        borderColor: 'rgba(34, 197, 94, 1)',
      },
      {
        label: 'Social',
        data: selectedTopics
          .filter(topic => topic.category === 'social')
          .map(topic => ({
            x: topic.financialScore,
            y: topic.impactScore,
            label: topic.name
          })),
        backgroundColor: 'rgba(59, 130, 246, 0.6)',
        borderColor: 'rgba(59, 130, 246, 1)',
      },
      {
        label: 'Governance',
        data: selectedTopics
          .filter(topic => topic.category === 'governance')
          .map(topic => ({
            x: topic.financialScore,
            y: topic.impactScore,
            label: topic.name
          })),
        backgroundColor: 'rgba(245, 158, 11, 0.6)',
        borderColor: 'rgba(245, 158, 11, 1)',
      }
    ];

    return { datasets };
  };

  const chartOptions = {
    scales: {
      x: {
        type: 'linear',
        position: 'bottom',
        min: 0,
        max: 5,
        title: {
          display: true,
          text: 'Financial Materiality',
          font: {
            size: 14,
            weight: 'bold'
          }
        },
        grid: {
          display: true,
          drawBorder: true,
          borderDash: [5, 5]
        }
      },
      y: {
        type: 'linear',
        min: 0,
        max: 5,
        title: {
          display: true,
          text: 'Impact Materiality',
          font: {
            size: 14,
            weight: 'bold'
          }
        },
        grid: {
          display: true,
          drawBorder: true,
          borderDash: [5, 5]
        }
      }
    },
    plugins: {
      tooltip: {
        callbacks: {
          label: (context) => {
            const point = context.raw;
            return `${point.label}: (${point.x}, ${point.y})`;
          }
        }
      },
      legend: {
        position: 'bottom',
        labels: {
          padding: 20,
          usePointStyle: true,
          pointStyle: 'circle'
        }
      },
      datalabels: {
        display: true,
        color: '#4a5568',
        anchor: 'end',
        align: 'top',
        offset: 5,
        font: {
          size: 11,
          weight: '500'
        },
        formatter: (value) => value.label
      },
      backgroundPlugin: {
        beforeDraw: (chart) => {
          const ctx = chart.ctx;
          const chartArea = chart.chartArea;
          const width = chartArea.right - chartArea.left;
          const height = chartArea.bottom - chartArea.top;
          const midX = chartArea.left + width / 2;
          const midY = chartArea.top + height / 2;

          // Draw quadrants with different colors
          // Bottom Left - Light Blue
          ctx.fillStyle = 'rgba(59, 130, 246, 0.1)';
          ctx.fillRect(chartArea.left, midY, width / 2, height / 2);

          // Top Left - Medium Blue
          ctx.fillStyle = 'rgba(59, 130, 246, 0.2)';
          ctx.fillRect(chartArea.left, chartArea.top, width / 2, height / 2);

          // Bottom Right - Medium Blue
          ctx.fillStyle = 'rgba(59, 130, 246, 0.2)';
          ctx.fillRect(midX, midY, width / 2, height / 2);

          // Top Right - Dark Blue
          ctx.fillStyle = 'rgba(59, 130, 246, 0.3)';
          ctx.fillRect(midX, chartArea.top, width / 2, height / 2);
        }
      }
    },
    maintainAspectRatio: true,
    aspectRatio: 1,
    responsive: true
  };

  return (
    <div className="page-wrapper">
      <main className="dma-container">
        <h1>Materiality Analysis</h1>
        <div className="dma-content">
          <div className="materiality-grid">
            {/* Topic Selection Section */}
            <section className="dma-section topic-selection">
              <h2>Topic Selection</h2>
              <div className="category-tabs">
                {Object.keys(topicSuggestions).map(category => (
                  <button
                    key={category}
                    className={`category-tab ${selectedCategory === category ? 'active' : ''}`}
                    onClick={() => setSelectedCategory(category)}
                  >
                    {category.charAt(0).toUpperCase() + category.slice(1)}
                  </button>
                ))}
              </div>

              <div className="topic-input">
                <input
                  type="text"
                  className="topic-input-field"
                  placeholder="Enter a sustainability topic..."
                  value={newTopic}
                  onChange={(e) => setNewTopic(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addTopic()}
                />
                <button 
                  className="add-topic-btn"
                  onClick={() => addTopic()}
                >
                  Add Topic
                </button>
              </div>

              <div className="topic-suggestions">
                <h3>Suggested {selectedCategory.charAt(0).toUpperCase() + selectedCategory.slice(1)} Topics</h3>
                <div className="suggestions-grid">
                  {topicSuggestions[selectedCategory].map((topic, index) => (
                    <button
                      key={index}
                      className="suggestion-item"
                      onClick={() => addTopic(topic)}
                    >
                      {topic}
                    </button>
                  ))}
                </div>
              </div>

              <div className="selected-topics">
                <h3>Selected Topics</h3>
                <div className="topics-list">
                  {selectedTopics.map((topic) => (
                    <div key={topic.id} className="topic-item">
                      <span>{topic.name}</span>
                      <select 
                        className="category-select"
                        value={topic.category}
                        onChange={(e) => {
                          const updatedTopics = selectedTopics.map(t => 
                            t.id === topic.id ? {...t, category: e.target.value} : t
                          );
                          setSelectedTopics(updatedTopics);
                        }}
                      >
                        <option value="environmental">Environmental</option>
                        <option value="social">Social</option>
                        <option value="governance">Governance</option>
                      </select>
                      <button 
                        className="delete-topic-btn"
                        onClick={() => removeTopic(topic.id)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Impact Assessment Section */}
            <section className="dma-section impact-assessment">
              <h2>Impact Assessment</h2>
              <div className="assessment-list">
                {selectedTopics.map((topic) => (
                  <div key={topic.id} className="assessment-item">
                    <div className="topic-name">{topic.name}</div>
                    <div className="assessment-inputs">
                      <div className="input-group">
                        <label>Financial Materiality (0-5):</label>
                        <input
                          type="range"
                          min="0"
                          max="5"
                          step="1"
                          value={topic.financialScore}
                          onChange={(e) => {
                            const updatedTopics = selectedTopics.map(t =>
                              t.id === topic.id ? {...t, financialScore: parseInt(e.target.value)} : t
                            );
                            setSelectedTopics(updatedTopics);
                          }}
                        />
                        <span>{topic.financialScore}</span>
                      </div>
                      <div className="input-group">
                        <label>Impact Materiality (0-5):</label>
                        <input
                          type="range"
                          min="0"
                          max="5"
                          step="1"
                          value={topic.impactScore}
                          onChange={(e) => {
                            const updatedTopics = selectedTopics.map(t =>
                              t.id === topic.id ? {...t, impactScore: parseInt(e.target.value)} : t
                            );
                            setSelectedTopics(updatedTopics);
                          }}
                        />
                        <span>{topic.impactScore}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Matrix Visualization Section */}
            <section className="dma-section matrix-section">
              <h2>Materiality Matrix</h2>
              <div className="matrix-container">
                <div className="matrix-visualization">
                  <Scatter data={getChartData()} options={chartOptions} />
                </div>

                <div className="matrix-explanation">
                  <h3>Matrix Interpretation</h3>
                  <ul>
                    <li>Top-right quadrant: High priority topics (high financial and impact materiality)</li>
                    <li>Top-left quadrant: Topics with high impact but lower financial materiality</li>
                    <li>Bottom-right quadrant: Topics with high financial but lower impact materiality</li>
                    <li>Bottom-left quadrant: Lower priority topics</li>
                  </ul>
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
};

export default MaterialityAnalysis;